import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, RotateCcw, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const cls = "w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2 font-sans text-sm focus:border-gold focus:outline-none";
const TEAM = { support: "Bookings · reservations@", hr: "Careers · hr@ (John Williams)" };

export default function AdminEmailTemplates() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/admin/email-templates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put(`/admin/email-templates/${edit.key}`, { subject: edit.subject, body: edit.body, enabled: edit.enabled }); toast.success("Template saved"); setEdit(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const reset = async () => {
    if (!window.confirm("Restore the original wording for this email?")) return;
    await api.post(`/admin/email-templates/${edit.key}/reset`); toast.success("Restored default"); setEdit(null); load();
  };
  const doPreview = async () => {
    const { data } = await api.post(`/admin/email-templates/${edit.key}/preview`, { subject: edit.subject, body: edit.body, enabled: true });
    setPreview(data);
  };

  return (
    <div>
      <p className="eyebrow">Communications</p>
      <h1 className="mt-3 font-serif text-3xl tracking-tight">Email Templates</h1>
      <p className="mt-2 font-sans text-sm text-[#94A3B8]">Every automatic email, when it is sent and its wording. Use {"{{variable}}"} placeholders shown on each template.</p>
      {["support", "hr"].map((team) => (
        <section key={team} className="mt-8">
          <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-gold">{TEAM[team]}</p>
          <div className="overflow-hidden rounded-lg border border-white/5">
            {items.filter((t) => t.team === team).map((t) => (
              <div key={t.key} data-testid={`tpl-row-${t.key}`} className="flex flex-wrap items-center gap-4 border-t border-white/5 bg-[#121722] px-5 py-4 first:border-t-0">
                <div className="min-w-[240px] flex-1">
                  <p className="font-sans text-sm">{t.name} {t.internal && <span className="ml-2 rounded-full border border-white/10 px-2 py-0.5 font-mono text-[9px] uppercase text-[#94A3B8]">internal</span>}</p>
                  <p className="mt-1 font-sans text-xs text-[#94A3B8]">Trigger: {t.trigger}</p>
                  <p className="mt-1 font-sans text-xs text-[#64748B]">Subject: {t.subject}</p>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-widest ${t.enabled ? "text-emerald-300" : "text-rose-300"}`}>{t.enabled ? "On" : "Off"}</span>
                <button onClick={() => { setEdit({ ...t }); setPreview(null); }} data-testid={`tpl-edit-${t.key}`} className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Pencil size={13} /> Edit</button>
              </div>
            ))}
          </div>
        </section>
      ))}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{edit?.name}</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">Trigger: {edit?.trigger}</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <label className="flex items-center gap-3 font-sans text-sm"><Switch checked={edit.enabled} onCheckedChange={(v) => setEdit({ ...edit, enabled: v })} data-testid="tpl-enabled" /> Send this email</label>
              <div className="flex flex-wrap gap-1.5">{edit.variables?.map((v) => <code key={v} className="rounded bg-gold/10 px-2 py-0.5 font-mono text-[11px] text-gold">{`{{${v}}}`}</code>)}</div>
              <label className="block font-sans text-xs text-[#94A3B8]">Subject</label>
              <input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} className={cls} data-testid="tpl-subject" />
              <label className="block font-sans text-xs text-[#94A3B8]">Body (HTML allowed; signature and logo are added automatically)</label>
              <textarea rows={10} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className={`${cls} font-mono text-xs`} data-testid="tpl-body" />
              {preview && (
                <div className="rounded-md border border-white/10 bg-white p-5 text-black" data-testid="tpl-preview">
                  <p className="mb-3 border-b pb-2 font-sans text-sm font-semibold">{preview.subject}</p>
                  <div className="font-serif text-sm" dangerouslySetInnerHTML={{ __html: preview.body }} />
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button onClick={reset} data-testid="tpl-reset" className="mr-auto flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 font-sans text-xs text-[#94A3B8] hover:text-gold"><RotateCcw size={13} /> Restore default</button>
                <button onClick={doPreview} data-testid="tpl-preview-btn" className="flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 font-sans text-xs text-gold"><Eye size={13} /> Preview</button>
                <button onClick={save} data-testid="tpl-save" className="rounded-full bg-gold px-5 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14]">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
